import { isRouteErrorResponse } from 'react-router-dom';

const DETAIL_LIMIT = 200;
const KEY_LIMIT = 10;

/**
 * Normalises a thrown value into an `Error` whose message names the failure.
 * `String()` on a framework object gives only its type tag (`[object Response]`,
 * and `[object Object]` for React Router's `ErrorResponse`), which is all Sentry
 * would show. Never serialises values (they can carry user data). Only
 * status, class names and keys go into the message.
 */
export function toCapturableError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;

  return new Error(describeThrown(thrown), { cause: thrown });
}

export function describeThrown(thrown: unknown): string {
  if (thrown instanceof Response) {
    return `Route threw HTTP ${thrown.status}${thrown.statusText === '' ? '' : `: ${thrown.statusText}`}`;
  }

  if (isRouteErrorResponse(thrown)) return describeErrorResponse(thrown);

  if (typeof thrown === 'string') return `Non-Error thrown (string): ${cut(thrown)}`;

  if (thrown === null) return 'Non-Error thrown (null)';

  if (thrown === undefined) return 'Non-Error thrown (undefined)';

  if (typeof thrown !== 'object' && typeof thrown !== 'function') {
    return `Non-Error thrown (${typeof thrown}): ${String(thrown)}`;
  }

  return describeObject(thrown);
}

function describeErrorResponse(response: { status: number; statusText: string; data: unknown }): string {
  const base = `Route threw HTTP ${response.status}`;

  if (response.statusText !== '') return `${base}: ${cut(response.statusText)}`;

  if (typeof response.data === 'string' && response.data !== '') return `${base}: ${cut(response.data)}`;

  return base;
}

function describeObject(thrown: object): string {
  const name = (thrown as { constructor?: { name?: unknown } }).constructor?.name;
  const className = typeof name === 'string' && name !== '' ? name : 'Object';
  const keys = Object.keys(thrown).slice(0, KEY_LIMIT);

  if (keys.length === 0) return `Non-Error thrown (${className}) with no keys`;

  return `Non-Error thrown (${className}) with keys: ${keys.join(', ')}`;
}

function cut(value: string): string {
  return value.length > DETAIL_LIMIT ? `${value.slice(0, DETAIL_LIMIT)}…` : value;
}
